/**
 * Spec 041 — FleetTab component.
 *
 * Rendered inside CommandCenterPanel when the "Fleet" tab is active (FR-004).
 * Displays browser-agent fleet members with the same activity indicator colors
 * the extension renders today: pulsing-green (active), amber (idle), grey (offline).
 *
 * Accepts pre-fetched data from useFleet; does not poll independently.
 */

import React, { useState } from 'react';
import type { FleetMember } from '../hooks/useFleet';

interface FleetTabProps {
  fleet: FleetMember[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
}

// ---------------------------------------------------------------------------
// Activity indicator color + label
// ---------------------------------------------------------------------------

const STATUS_COLOR: Record<string, string> = {
  active: '#33AA40',
  idle: '#F5A623',
  offline: '#8899A6',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  idle: 'Idle',
  offline: 'Offline',
};

const AGENT_TYPE_LABEL: Record<string, string> = {
  comet: 'Comet',
  cursor: 'Cursor',
  gateway: 'Gateway',
};

function fmtRelative(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface FleetHierarchy {
  display: string;
  window: string;
  tabGroup: string;
}

interface FleetGroup {
  key: string;
  hierarchy: FleetHierarchy;
  members: FleetMember[];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function readString(record: Record<string, unknown> | null, keys: string[]): string | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return null;
}

function getFleetHierarchy(member: FleetMember): FleetHierarchy {
  const metadata = member.metadata ?? {};
  const session = asRecord(metadata.browserSession)
    ?? asRecord(metadata.session)
    ?? asRecord(metadata.surface)
    ?? metadata;

  return {
    display: readString(metadata, ['displayName', 'display', 'displayLabel', 'displayId', 'screenName'])
      ?? readString(session, ['displayName', 'display', 'displayLabel', 'displayId', 'screenName'])
      ?? 'Unknown display',
    window: readString(metadata, ['windowName', 'windowTitle', 'windowId', 'window'])
      ?? readString(session, ['windowName', 'windowTitle', 'windowId', 'window'])
      ?? 'Unknown window',
    tabGroup: readString(metadata, ['tabGroupName', 'tabGroupTitle', 'tabGroupId', 'tabGroup'])
      ?? readString(session, ['tabGroupName', 'tabGroupTitle', 'tabGroupId', 'tabGroup'])
      ?? member.sessionDisplayName
      ?? 'No tab group',
  };
}

function groupFleet(fleet: FleetMember[]): FleetGroup[] {
  const groups = new Map<string, FleetGroup>();

  fleet.forEach(member => {
    const hierarchy = getFleetHierarchy(member);
    const key = JSON.stringify([hierarchy.display, hierarchy.window, hierarchy.tabGroup]);
    const group = groups.get(key);
    if (group) {
      group.members.push(member);
      return;
    }
    groups.set(key, { key, hierarchy, members: [member] });
  });

  return Array.from(groups.values()).sort((a, b) => a.key.localeCompare(b.key));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FleetTab: React.FC<FleetTabProps> = ({ fleet, isLoading, error, onRefresh }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0,
  };

  const headerLabelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    color: '#8899A6',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const iconBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#8899A6',
    padding: '2px 4px',
    borderRadius: '3px',
    fontSize: '16px',
    lineHeight: 1,
  };

  const scrollStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 0',
  };

  const emptyStyle: React.CSSProperties = {
    padding: '24px 16px',
    textAlign: 'center',
    color: '#8899A6',
    fontSize: '12px',
    lineHeight: 1.5,
  };

  const memberRowStyle = (isExpanded: boolean): React.CSSProperties => ({
    padding: '7px 12px',
    cursor: 'pointer',
    backgroundColor: isExpanded ? 'rgba(255,255,255,0.04)' : 'transparent',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  });

  const memberHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const dotStyle = (status: string, active: boolean): React.CSSProperties => ({
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    backgroundColor: STATUS_COLOR[status] ?? '#8899A6',
    boxShadow: active ? `0 0 5px ${STATUS_COLOR[status] ?? '#8899A6'}` : 'none',
    animation: active ? 'pulse 2s infinite' : 'none',
    flexShrink: 0,
  });

  const agentNameStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 500,
    color: '#C8D6DF',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const typeLabelStyle: React.CSSProperties = {
    fontSize: '10px',
    color: '#8899A6',
    flexShrink: 0,
  };

  const expandedDetailStyle: React.CSSProperties = {
    marginTop: '6px',
    paddingLeft: '15px',
    fontSize: '10px',
    color: '#8899A6',
    lineHeight: 1.6,
  };

  const hierarchyHeaderStyle: React.CSSProperties = {
    padding: '8px 12px 4px',
    fontSize: '10px',
    color: '#8899A6',
    backgroundColor: 'rgba(0,0,0,0.12)',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    lineHeight: 1.5,
  };

  const hierarchyPathStyle: React.CSSProperties = {
    color: '#C8D6DF',
    fontWeight: 600,
  };

  const errorStyle: React.CSSProperties = {
    padding: '4px 12px',
    fontSize: '11px',
    color: '#FF5656',
    backgroundColor: 'rgba(255,86,86,0.08)',
  };

  // Count by status for header summary
  const activeCount = fleet.filter(f => f.status === 'active').length;
  const idleCount = fleet.filter(f => f.status === 'idle').length;
  const groupedFleet = groupFleet(fleet);

  const handleMemberKeyDown = (memberId: string, isExpanded: boolean, e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    setExpandedId(isExpanded ? null : memberId);
  };

  return (
    <div style={containerStyle}>
      {/* Inject keyframes once globally */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={headerLabelStyle}>Fleet</span>
          {fleet.length > 0 && (
            <span style={{ fontSize: '10px', color: '#8899A6' }}>
              {activeCount > 0 && <span style={{ color: '#33AA40' }}>{activeCount} active</span>}
              {activeCount > 0 && idleCount > 0 && <span> · </span>}
              {idleCount > 0 && <span style={{ color: '#F5A623' }}>{idleCount} idle</span>}
            </span>
          )}
        </div>
        <button
          type="button"
          style={iconBtnStyle}
          onClick={onRefresh}
          title="Refresh fleet"
          aria-label="Refresh fleet"
        >
          ↻
        </button>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      <div style={scrollStyle}>
        {isLoading && fleet.length === 0 ? (
          <div style={emptyStyle}>Loading fleet…</div>
        ) : fleet.length === 0 ? (
          <div style={emptyStyle}>
            <div style={{ marginBottom: '8px' }}>No agents registered for this organization.</div>
            <div style={{ fontSize: '11px', color: '#6B7A83' }}>
              Register an Equabot by running{' '}
              <code style={{ color: '#4DC95A', backgroundColor: 'rgba(0,0,0,0.2)', padding: '1px 4px', borderRadius: '3px' }}>
                agent-register.mjs
              </code>{' '}
              in your Comet session or Cursor terminal.
            </div>
          </div>
        ) : (
          groupedFleet.map(group => (
            <div key={group.key}>
              <div style={hierarchyHeaderStyle}>
                <div style={hierarchyPathStyle}>
                  {group.hierarchy.display} / {group.hierarchy.window} / {group.hierarchy.tabGroup}
                </div>
                <div>{group.members.length} agent{group.members.length === 1 ? '' : 's'}</div>
              </div>
              {group.members.map(member => {
                const isExpanded = expandedId === member.id;
                const displayName = member.displayName ?? member.agentId.slice(0, 20);
                return (
                  <div
                    key={member.id}
                    role="button"
                    tabIndex={0}
                    style={memberRowStyle(isExpanded)}
                    onClick={() => setExpandedId(isExpanded ? null : member.id)}
                    onKeyDown={e => handleMemberKeyDown(member.id, isExpanded, e)}
                  >
                    <div style={memberHeaderStyle}>
                      <div style={dotStyle(member.status, member.status === 'active')} aria-hidden="true" />
                      <span style={agentNameStyle} title={member.agentId}>{displayName}</span>
                      <span style={typeLabelStyle}>{AGENT_TYPE_LABEL[member.agentType] ?? member.agentType}</span>
                      <span style={{ fontSize: '10px', color: STATUS_COLOR[member.status] ?? '#8899A6' }}>
                        {STATUS_LABEL[member.status] ?? member.status}
                      </span>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={expandedDetailStyle}>
                        <div>Agent ID: <span style={{ fontFamily: 'monospace', color: '#C8D6DF' }}>{member.agentId}</span></div>
                        {member.sessionDisplayName && (
                          <div>Session: <span style={{ color: '#C8D6DF' }}>{member.sessionDisplayName}</span></div>
                        )}
                        {member.taskGoal && (
                          <div>Goal: <span style={{ color: '#C8D6DF' }}>{member.taskGoal}</span></div>
                        )}
                        <div>Heartbeat: {fmtRelative(member.lastHeartbeat)}</div>
                        {Object.keys(member.metadata).length > 0 && (
                          <div>
                            Metadata:{' '}
                            <span style={{ fontFamily: 'monospace', color: '#C8D6DF', fontSize: '9px' }}>
                              {JSON.stringify(member.metadata).slice(0, 60)}…
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FleetTab;
